import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { download, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, PageHeader, Empty, Loading, ErrorNote, inputCls, btn, StatusChip } from "../components/kit";
import AssignDialog from "../components/AssignDialog";
import { ExternalPanel } from "../components/ExternalTraining";
import { TeamGrid } from "../components/TeamGrid";
import { DriversTab } from "../components/Drivers";
import { TeamMemberDialog } from "../components/TeamMember";

export default function Team() {
  const { user } = useAuth();
  const [all, setAll] = useState(false);
  const [tab, setTab] = useState("people");
  const { data, loading, error, reload } = useApi(`/team?all=${all}`);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [flt, setFlt] = useState("");
  const rows = (data || []).filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) && (!dept || r.department === dept)
    && (!flt || (flt === "overdue" ? r.overdue : flt === "outstanding" ? r.outstanding : flt === "awaiting" ? r.certs_awaiting : r.expiries)));
  const depts = [...new Set((data || []).map((r) => r.department).filter(Boolean))];
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Manager" title="My Team" testid="team-header">
        <TeamMemberDialog onSaved={reload} />
        <AssignDialog onDone={reload} />
        <button className={btn.ghost} data-testid="team-export-button" onClick={() => download(`/team/export?all=${all}`, "team.csv").catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" /> CSV</button>
      </PageHeader>
      <nav className="flex gap-1 border-b" aria-label="My Team views">
        {[["people", "People"], ["grid", "Training grid"], ["drivers", "Drivers"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`team-tab-${k}`}
            className={`tap border-b-2 px-4 text-sm font-semibold ${tab === k ? "border-[color:var(--dcbc-red)]" : "border-transparent text-muted-foreground"}`}>{l}</button>))}
      </nav>
      {user.is_superadmin && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="team-all-toggle" className="h-5 w-5" /> Show everyone in the organization</label>}
      {tab === "drivers" ? <DriversTab all={all} /> : tab === "grid" ? <TeamGrid all={all} /> : <>
      <div className="grid gap-3 sm:grid-cols-[1fr_200px_220px]">
        <input className={inputCls} placeholder="Search by name" aria-label="Search team" value={q} onChange={(e) => setQ(e.target.value)} data-testid="team-search-input" />
        <select className={inputCls} aria-label="Department" value={dept} onChange={(e) => setDept(e.target.value)} data-testid="team-dept-filter"><option value="">All departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
        <select className={inputCls} aria-label="Show" value={flt} onChange={(e) => setFlt(e.target.value)} data-testid="team-status-filter">
          <option value="">Everyone</option><option value="overdue">Has overdue training</option><option value="outstanding">Has outstanding training</option><option value="awaiting">Certificates awaiting review</option><option value="expiries">Upcoming expiries</option>
        </select>
      </div>
      <ErrorNote error={error} />
      {loading ? <Loading /> : rows.length === 0 ? <Empty testid="team-empty">Nobody matches. People appear here when the Superadmin makes you their manager.</Empty> : (
        <div className="grid gap-3" data-testid="team-list">
          {rows.map((r) => (
            <Link key={r.id} to={`/team/${r.id}`} data-testid={`team-row-${r.id}`} className="group grid gap-3 rounded-xl border bg-white p-4 hover:border-[color:var(--ink)] transition-colors md:grid-cols-[1.4fr_2fr_auto] md:items-center">
              <div><p className="font-semibold">{r.name}</p><p className="text-xs text-muted-foreground">{r.employee_id} · {r.department || "—"} · {r.roles.join(", ") || "No roles"}</p></div>
              <div className="flex flex-wrap gap-2">
                {r.overdue > 0 && <StatusChip status="overdue" label={`${r.overdue} overdue`} />}
                <StatusChip status={r.outstanding ? "in_progress" : "not_started"} label={`${r.outstanding} outstanding`} />
                <StatusChip status="completed" label={`${r.completed} complete`} />
                {r.policies_outstanding > 0 && <StatusChip status="not_started" label={`${r.policies_outstanding} polic${r.policies_outstanding === 1 ? "y" : "ies"} to sign`} />}
                {r.certs_awaiting > 0 && <StatusChip status="awaiting_review" label={`${r.certs_awaiting} cert to review`} />}
                {r.expiries > 0 && <StatusChip status="expiring_soon" label={`${r.expiries} expiring`} />}
              </div>
              <ChevronRight className="hidden md:block h-5 w-5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ))}
        </div>
      )}
      <div className="mt-8"><ExternalPanel /></div>
      </>}
    </div>
  );
}
